import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import router from '../../routes/index.jsx';
import { useCommandPaletteSearch, isAIQuery } from './useCommandPaletteSearch.js';
import { useCommandPaletteActions } from './CommandPaletteActions.jsx';
import { useCommandPaletteSuggestions } from './CommandPaletteSuggestions.jsx';
import { CommandPaletteAI } from './CommandPaletteAI.jsx';
import './CommandPalette.css';

const MIN_SEARCH = 2;

const modeOf = (query) => {
    const q = (query || '').trim();
    if (q.length < MIN_SEARCH) return 'default';
    if (isAIQuery(q)) return 'ai';
    return 'search';
};

export const CommandPalette = ({ onClose }) => {
    const [query, setQuery] = useState('');
    const [selectedIdx, setSelectedIdx] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const mode = modeOf(query);
    const searchResults = useCommandPaletteSearch(mode === 'search' ? query : '');
    const suggestions = useCommandPaletteSuggestions();
    const actions = useCommandPaletteActions();

    const groups = useMemo(() => {
        if (mode === 'default') {
            // Only the actions this user may take; the group is omitted when there are none.
            const defaultGroups = actions.length ? [{ title: 'Actions', items: actions }] : [];
            // Omit the group entirely when nothing is openable, rather than
            // rendering an empty "Suggested for you" header.
            if (suggestions.length) {
                defaultGroups.push({ title: 'Suggested for you', items: suggestions });
            }
            return defaultGroups;
        }
        if (mode === 'search') {
            if (searchResults.length === 0) return [];
            return [{ title: 'Results', items: searchResults }];
        }
        return [];
    }, [mode, searchResults, suggestions, actions]);

    const flatItems = useMemo(
        () => groups.flatMap(g => g.items),
        [groups]
    );

    useEffect(() => {
        setSelectedIdx(0);
    }, [mode, query]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const runItem = (item) => {
        if (!item) return;
        onClose();
        if (item.path) {
            router.navigate(item.path);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
        if (mode === 'ai') return;
        if (!flatItems.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIdx(i => (i + 1) % flatItems.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIdx(i => (i - 1 + flatItems.length) % flatItems.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            runItem(flatItems[selectedIdx]);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    let flatCursor = 0;

    return createPortal(
        <div className="cp-backdrop" onMouseDown={handleBackdropClick}>
            <div className="cp-container" role="dialog" aria-modal="true" aria-label="Command palette">
                <div className="cp-input-wrapper">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="cp-input-icon" />
                    <input
                        ref={inputRef}
                        className="cp-input"
                        placeholder="Search or ask a question..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoComplete="off"
                        spellCheck={false}
                    />
                    <kbd className="cp-shortcut">ESC</kbd>
                </div>

                <div className="cp-results" ref={listRef}>
                    {mode === 'ai' && <CommandPaletteAI query={query} />}

                    {mode !== 'ai' && groups.length === 0 && (
                        <div className="cp-empty">
                            <div className="cp-empty-title">No matching results</div>
                            <div className="cp-empty-sub">Try asking the Agent instead</div>
                            <button
                                className="cp-empty-agent"
                                onClick={() => setQuery(q => q.trim() ? q + ' ?' : 'How do I ')}
                            >
                                <FontAwesomeIcon icon={faWandMagicSparkles} />
                                Ask the Agent
                            </button>
                        </div>
                    )}

                    {mode !== 'ai' && groups.map((group) => (
                        <div key={group.title} className="cp-group">
                            <div className="cp-group-title">{group.title}</div>
                            <div className="cp-group-items">
                                {group.items.map((item) => {
                                    const idx = flatCursor++;
                                    const selected = idx === selectedIdx;
                                    return (
                                        <button
                                            key={item.id || item.path}
                                            type="button"
                                            className={`cp-row ${selected ? 'cp-row--selected' : ''}`}
                                            onMouseEnter={() => setSelectedIdx(idx)}
                                            onClick={() => runItem(item)}
                                        >
                                            <div className="cp-row-icon">{item.icon}</div>
                                            <div className="cp-row-body">
                                                <div className="cp-row-title">{item.label}</div>
                                                {item.subtitle && (
                                                    <div className="cp-row-sub">{item.subtitle}</div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="cp-footer">
                    <span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
                    <span><kbd>⏎</kbd> Select</span>
                    <span><kbd>ESC</kbd> Close</span>
                </div>
            </div>
        </div>,
        document.body
    );
};
